/**
 * Hook for drag-and-drop functionality
 * Handles drag start, drag end, trash zone, and reordering logic
 */

import { useState, useCallback } from 'react'
import {
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DragStartEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { DashboardWidget } from '../types'

interface UseDragAndDropProps {
    onAddWidget: (widgetId: string) => void
    setDashboardWidgets: React.Dispatch<React.SetStateAction<DashboardWidget[]>>
    saveLayout: (widgets: DashboardWidget[]) => void
}

interface UseDragAndDropReturn {
    activeId: string | null
    sensors: ReturnType<typeof useSensors>
    handleDragStart: (event: DragStartEvent) => void
    handleDragEnd: (event: DragEndEvent) => void
}

export function useDragAndDrop({
    onAddWidget,
    setDashboardWidgets,
    saveLayout,
}: UseDragAndDropProps): UseDragAndDropReturn {
    const [activeId, setActiveId] = useState<string | null>(null)

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveId(event.active.id as string)
    }, [])

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event
            const draggedId = active.id as string

            // Check if dragging from store
            const isFromStore = draggedId.startsWith('store-')

            // Handle store widget drop
            if (isFromStore) {
                // If dropped on trash or nowhere valid, ignore
                if (over?.id === 'trash-zone' || !over) {
                    setActiveId(null)
                    return
                }

                // Add widget to dashboard if dropped on dashboard grid or any widget
                const widgetId = draggedId.replace('store-', '')
                onAddWidget(widgetId)
                setActiveId(null)
                return
            }

            // Handle drop to trash zone (existing widget)
            if (over?.id === 'trash-zone') {
                setDashboardWidgets((items) => {
                    const updatedWidgets = items.map((item) =>
                        item.id === draggedId
                            ? { ...item, isVisible: false }
                            : item
                    )
                    saveLayout(updatedWidgets)
                    return updatedWidgets
                })
                setActiveId(null)
                return
            }

            // Handle reordering (existing widgets)
            if (draggedId !== over?.id && over?.id) {
                setDashboardWidgets((items) => {
                    const oldIndex = items.findIndex(
                        (item) => item.id === draggedId
                    )
                    const newIndex = items.findIndex(
                        (item) => item.id === over.id
                    )

                    if (oldIndex !== -1 && newIndex !== -1) {
                        const newOrder = arrayMove(items, oldIndex, newIndex)
                        const updatedWidgets = newOrder.map((item, index) => ({
                            ...item,
                            order: index,
                        }))
                        saveLayout(updatedWidgets)
                        return updatedWidgets
                    }
                    return items
                })
            }

            setActiveId(null)
        },
        [onAddWidget, setDashboardWidgets, saveLayout]
    )

    return {
        activeId,
        sensors,
        handleDragStart,
        handleDragEnd,
    }
}
